/**
 * Today — the day's numbers and the day's food, on one screen.
 *
 * Overview and Diary used to be separate destinations, and they were one screen
 * cut in half. Overview answered "how is today going"; Diary answered "what did
 * I eat"; and every single act of logging bounced between them, because the
 * answer to the first question only changes when you do something about the
 * second. Two thirds of the navigation was spent walking between two halves of
 * one thought.
 *
 * So: the ring, the macros and the day's slow signals live in a rail, the meals
 * are on the right, and **logging happens in place** — now in a sheet raised
 * over this screen rather than in a field under each meal. Same principle, one
 * step further: the sheet knows the meal and the date because the `+` that
 * raised it did, and the ring stays visible above it, so the effect of what you
 * just logged is on screen while you log the next thing.
 *
 * The rail is a rail and not a header because its content is slow: a ring and a
 * weight trend change once a day, while the meal list changes every time you
 * eat. Putting the slow things beside the fast ones rather than above them
 * means the part you are working in does not move down the page as the day
 * fills up.
 *
 * **Meals collapse.** A day with four meals in it did not fit an 812px phone,
 * so the screen you return to most was a screen you had to scroll to read. Each
 * meal is now one line — name, item count, calories — that opens on a tap, and
 * the rail is compacted to a 168px ring, one row of macro bars and a single
 * line of slow signals to pay for it. The count stays visible when the section
 * is shut: a collapsed meal must not be indistinguishable from an empty one.
 */

import {
  deviceTimeZone,
  inLateNight,
  localDate,
  localTime,
  nutrientTotals,
  shiftDate,
  useDay,
  useDeleteLog,
  useFood,
  useOverview,
  useProfile,
  useUpdateProfile,
  useWeightTrend,
  type FoodLog,
  type Meal,
} from "@calorio/core";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { moreOpen, setMoreOpen } from "./more-open";
import { openMeals, setOpenMeals } from "./open-meals";
import { keepZone, zoneKept } from "./zone-answer";
import { useOffline } from "../app/offline";
import { useCalorio } from "../app/providers";
import { AddSheet, type SheetMode } from "../components/AddSheet";
import { CorrectFood } from "../components/CorrectFood";
import { EntryEditor } from "../components/EntryEditor";
import { LateNight } from "../components/LateNight";
import { mealForHour } from "../components/MealPicker";
import { MacroBar } from "../components/MacroBar";
import { MealNutrients, NutrientDetail } from "../components/NutrientDetail";
import { Ring } from "../components/Ring";
import { TimezoneMismatch } from "../components/TimezoneMismatch";

const MEALS = ["breakfast", "lunch", "dinner", "snack"] as const;
const LABEL: Record<Meal, string> = {
  breakfast: "Breakfast",
  lunch: "Lunch",
  dinner: "Dinner",
  snack: "Snacks",
};

/**
 * The compacted ring.
 *
 * 168px rather than 232. The whole day has to fit one screen for the collapse
 * to have been worth doing, and a ring is the one element on Today that can
 * give up a third of its size and still be the first thing the eye lands on.
 */
const RING = 168;

export function Today() {
  const ctx = useCalorio();
  const navigate = useNavigate();
  const { date } = useParams();
  const today = localDate(ctx.timeZone);
  const shown = date ?? today;

  /**
   * Which day new entries are written to.
   *
   * Normally the day on screen. Between midnight and 04:00 it is a *choice* —
   * see `LateNight` — and the two can differ: you are reading Friday, which has
   * nothing on it yet, while logging the tail of Thursday evening.
   */
  const [writingTo, setWritingTo] = useState<string | null>(null);
  /**
   * Dismissing the timezone warning is a real answer, not a postponement.
   *
   * Held here so the banner leaves the moment it is answered, and written to
   * the device by `keepZone` so it does not come back on the next load. Both
   * are needed: the stored answer is keyed by a zone pairing that only exists
   * once the profile has loaded, and this screen must not flash a warning the
   * user settled a week ago.
   */
  const [zoneDismissed, setZoneDismissed] = useState(false);
  /** The food behind a logged row, when the user has asked to fix the product. */
  const [correcting, setCorrecting] = useState<string | null>(null);

  /**
   * The add sheet: null is closed, and a meal is both "open" and "which meal".
   *
   * One value rather than a boolean beside a meal, because the two can never
   * legitimately disagree — an open sheet with no meal has nowhere to write and
   * a meal with no sheet is nothing at all.
   */
  const [sheetMeal, setSheetMeal] = useState<Meal | null>(null);
  const [sheetMode, setSheetMode] = useState<SheetMode>("list");

  const day = useDay(ctx, shown);
  const overview = useOverview(ctx, 30);
  const weight = useWeightTrend(ctx, 90);
  const remove = useDeleteLog(ctx);
  const offline = useOffline();
  const profile = useProfile(ctx);
  const updateProfile = useUpdateProfile(ctx);

  // The card needs yesterday's close to make the choice an informed one, so it
  // is fetched only while the window is open and only for today's view.
  const lateNight = inLateNight(ctx.timeZone) && shown === today;
  const yesterday = shiftDate(today, -1);
  const lastNight = useDay(ctx, lateNight ? yesterday : today);

  const [editing, setEditing] = useState<string | null>(null);
  const correctingFood = useFood(ctx, correcting);

  /**
   * Which meal sections are open, as the user last left this day.
   *
   * Defaulted from the clock, not to "all open" — the meal you are about to log
   * into is nearly always the one the hour suggests, and opening all four is
   * the layout the collapse exists to avoid. Seeded lazily so the read happens
   * once rather than on every render, and re-seeded when the date changes,
   * because each day carries its own arrangement.
   */
  const [open, setOpen] = useState<Meal[]>(
    () => openMeals(shown) ?? [mealForHour(new Date().getHours())],
  );
  useEffect(() => {
    setOpen(openMeals(shown) ?? [mealForHour(new Date().getHours())]);
  }, [shown]);

  /**
   * Whether the fuller breakdown is open. Read once, and unlike the meals it
   * is not per day — see `more-open.ts`.
   */
  const [detailOpen, setDetailOpen] = useState(moreOpen);

  const toggle = (meal: Meal) => {
    setOpen((current) => {
      const next = current.includes(meal)
        ? current.filter((m) => m !== meal)
        : [...current, meal];
      setOpenMeals(shown, next);
      return next;
    });
  };

  if (day.isLoading) return <p className="muted">Loading your day…</p>;
  if (day.error) return <p className="error">Couldn't load that day. {String(day.error)}</p>;
  if (!day.data) return null;

  const { totals, target, entries, local_date } = day.data;
  // Derived from the entries rather than read off the day, so an entry logged
  // offline moves this the instant it is written — `patchDay` rewrites the
  // entry list and these follow, with nothing to keep in step separately.
  const dayNutrients = nutrientTotals(entries);
  const left = target ? target.kcal - totals.kcal : null;
  const logTo = writingTo ?? local_date;

  const profileZone = profile.data?.timezone ?? null;
  const device = deviceTimeZone();
  // Answered here before, and answered for this exact pairing of zones: a
  // profile that has since moved, or a device that has, is a new question.
  const zoneMismatch =
    !zoneDismissed &&
    profileZone !== null &&
    device.length > 0 &&
    profileZone !== device &&
    !zoneKept(profileZone, device);

  /**
   * A row still in the queue.
   *
   * The design marks the row rather than raising an alarm, and says so in words
   * next to the weight — "64 g · waiting to sync" — because a coloured dot on
   * its own is not information, it is anxiety.
   */
  const waiting = (entry: FoodLog) => offline.pendingIds.has(entry.id);

  /**
   * What is already on the day the sheet will write to.
   *
   * Usually the day on screen, and between midnight and 04:00 it can be
   * yesterday — the sheet's "already logged at 380 g" has to be about the plate
   * being written to, not the one being read. `lastNight` is only fetched
   * during that window, so anything else falls back to nothing, which costs a
   * notice rather than risking one about the wrong day.
   */
  const loggedOnWritingDay =
    logTo === local_date
      ? entries
      : lastNight.data?.local_date === logTo
        ? lastNight.data.entries
        : [];

  /** Raise the sheet, at a meal and in a mode. */
  const raise = (meal: Meal, mode: SheetMode = "list") => {
    setSheetMeal(meal);
    setSheetMode(mode);
  };

  return (
    <div className="today">
      {/* One strip, not a per-row alarm. The count is the whole message: the
          entries are safe, they are going to send themselves, and there is
          nothing here for the user to do. */}
      {offline.pending > 0 && (
        <div className="queued-strip">
          <span className="queued-dot" aria-hidden="true" />
          <span>
            Waiting to sync — <span className="num">{offline.pending}</span>{" "}
            {offline.pending === 1 ? "entry" : "entries"}
          </span>
          <button className="queued-strip__action" onClick={() => offline.retry()}>
            Retry now
          </button>
        </div>
      )}

      {/* The silent bug first: a profile in the wrong zone files every evening
          meal on the following day, forever, and nothing else in the app can
          see it. Today can see both zones, so Today says so. */}
      {zoneMismatch && profileZone && (
        <TimezoneMismatch
          profileZone={profileZone}
          deviceZone={device}
          saving={updateProfile.isPending}
          onAdopt={() =>
            updateProfile.mutate(
              { timezone: device },
              { onSuccess: () => setZoneDismissed(true) },
            )
          }
          onKeep={() => {
            // Recorded against both zones, so this device stops asking while
            // the two stay as they are — and asks again if either moves.
            keepZone(profileZone, device);
            setZoneDismissed(true);
          }}
        />
      )}

      <div className="today__body">
        <aside className="today__rail stack">
          <header className="today__header">
            {/* Three columns, the outer two equal, so the date sits on the
                centre line of the rail rather than wherever the arrow before it
                happens to end. The arrows are the same width as each other and
                the heading is what the eye lands on. */}
            <div className="today__date">
              <Link
                to={`/day/${shiftDate(local_date, -1)}`}
                className="muted today__step"
                aria-label="Previous day"
              >
                ‹
              </Link>
              <h1 className="today__heading">
                {new Date(`${local_date}T12:00:00Z`).toLocaleDateString(undefined, {
                  weekday: "short",
                  day: "numeric",
                  month: "short",
                })}
              </h1>
              <Link
                to={`/day/${shiftDate(local_date, 1)}`}
                className="muted today__step"
                aria-label="Next day"
              >
                ›
              </Link>
            </div>

            <p className="stat__label today__meta" style={{ margin: 0 }}>
              {/* The clock, but only while the boundary is in question. A time
                  in the header at any other hour is decoration; here it is the
                  evidence for the card below. It sits on the meta line rather
                  than beside the date, which would push the date off centre for
                  four hours a night. */}
              {lateNight && (
                <>
                  <span className="num">{localTime(ctx.timeZone)}</span>
                  {" · "}
                </>
              )}
              {overview.data?.goal && (
                <>
                  <span className="num">Day {overview.data.goal.day}</span>
                  {overview.data.goal.total_days && (
                    <span className="num"> of {overview.data.goal.total_days}</span>
                  )}
                </>
              )}
              {overview.data && overview.data.streak.current > 0 && (
                <>
                  {overview.data.goal ? " · " : ""}
                  <span aria-hidden="true">🔥</span>{" "}
                  <span className="num">{overview.data.streak.current}</span>
                </>
              )}
              {!overview.data?.goal && (
                <>
                  {/* A separator, because a streak and this link land on the
                      same line and read as one word without it: "🔥 2Set a
                      goal". */}
                  {overview.data && overview.data.streak.current > 0 ? " · " : ""}
                  <Link to="/you#goals" className="muted">
                    Set a goal →
                  </Link>
                </>
              )}
            </p>
          </header>

          <Ring consumed={totals} targetKcal={target?.kcal ?? null} size={RING} />

          {/* The queue is already counted in the figure above — `patchDay` writes
              the optimistic row before anything is sent. Saying so matters: a
              total that moved when the network dropped would read as food going
              missing, and one that silently did not would be wrong. */}
          {offline.pending > 0 && <p className="today__note">Includes the queued entries.</p>}

          {!target && (
            <p className="stat__label" style={{ textAlign: "center", margin: 0 }}>
              <Link to="/you#goals" className="muted">
                Set a daily budget →
              </Link>
            </p>
          )}

          {/* Three across rather than three down. Stacked, the bars cost 96px of
              the height the meals need; in a row they cost 34px and lose
              nothing — a bar is read by its length, and three short ones side by
              side compare more directly than three long ones in a column. */}
          <div className="today__macros">
            <MacroBar
              kind="protein"
              label="Protein"
              grams={totals.protein_g}
              targetGrams={target?.protein_g ?? null}
              compact
            />
            <MacroBar
              kind="carbs"
              label="Carbs"
              grams={totals.carbs_g}
              targetGrams={target?.carbs_g ?? null}
              compact
            />
            <MacroBar
              kind="fat"
              label="Fat"
              grams={totals.fat_g}
              targetGrams={target?.fat_g ?? null}
              compact
            />
          </div>

          {/* Everything the packet says that the ring does not. Collapsed by
              default and stated in coverage rather than drawn as a bar — see
              NutrientDetail for why both of those are deliberate. Nothing to
              show on an empty day, so nothing is shown. */}
          {entries.length > 0 && (
            <NutrientDetail
              totals={dayNutrients}
              open={detailOpen}
              onToggle={() => {
                setDetailOpen(!detailOpen);
                setMoreOpen(!detailOpen);
              }}
            />
          )}

          {/* Weight and the weekly average: the day's slow signals, in the rail
              because they answer "is this working" rather than "what have I
              eaten", and those are different questions asked at different
              rates. The headline is the smoothed trend, not the last reading —
              see the Weight page for why. One line rather than two cards: they
              are two figures and a direction each, and a card apiece was the
              height of a meal section. */}
          <div className="today__slow">
            <Link to="/weight" className="today__slow-cell">
              <span className="stat__label">Weight</span>
              <span className="today__slow-figure num">
                {weight.data?.entries.length
                  ? `${weight.data.entries.at(-1)?.weight_kg.toFixed(1)}`
                  : "—"}
              </span>
              {weight.data?.change_kg != null && (
                <span
                  className={`num ${weight.data.change_kg <= 0 ? "delta--down" : "delta--up"}`}
                >
                  {weight.data.change_kg > 0 ? "+" : ""}
                  {weight.data.change_kg.toFixed(1)}
                </span>
              )}
            </Link>

            <Link to="/review" className="today__slow-cell">
              <span className="stat__label">7-day</span>
              <span className="today__slow-figure num">
                {overview.data?.intake.average_kcal
                  ? Math.round(overview.data.intake.average_kcal).toLocaleString()
                  : "—"}
              </span>
              {overview.data?.intake.average_vs_target != null && (
                <span
                  className={`num ${
                    overview.data.intake.average_vs_target <= 0 ? "delta--down" : "delta--up"
                  }`}
                >
                  {overview.data.intake.average_vs_target > 0 ? "+" : ""}
                  {Math.round(overview.data.intake.average_vs_target)}
                </span>
              )}
            </Link>
          </div>

          {left !== null && (
            <p className="stat__label num" style={{ margin: 0 }}>
              {Math.round(totals.kcal).toLocaleString()} / {target?.kcal.toLocaleString()} kcal
            </p>
          )}
        </aside>

        <div className="today__meals">
          {lateNight && (
            <LateNight
              today={today}
              yesterday={yesterday}
              yesterdaySummary={lastNight.data}
              writingTo={logTo}
              onChoose={setWritingTo}
            />
          )}

          {/* Day one. The ring above is a bare track with the whole budget
              inside it, which is honest — nothing has been logged — but a bare
              track says nothing about what to do next. The three ways in are on
              the screen rather than behind a tab bar somebody has to discover,
              and the first carries the border because it is the one the app is
              actually for.

              Each one now raises the sheet in the matching mode rather than
              pushing a screen. The meal comes from the clock and is changeable
              in the sheet's own header, which is the only place on day one that
              could ask — there are no meal sections yet to press a `+` under.

              The lead sentence has to know whether there is a budget, because
              without one `ringModel` draws no macro arcs at all and titles the
              ring "kcal eaten" — correct, and the state every new account is
              in. Promising a split that cannot happen would have the first
              thing somebody logs disprove the sentence they were just handed.
              The rail above already carries the way to fix it, so this says
              what the ring will do and leaves the link where it is. */}
          {!entries.length && (
            <div className="dayone">
              <p className="dayone__lead">
                {target
                  ? "Log one thing and the ring splits into protein, carbs and fat."
                  : "Log one thing and the ring starts counting what you've eaten; " +
                    "give it a daily budget and it splits into protein, carbs and fat."}{" "}
                Log a week and Review and Plan have something to work with.
              </p>
              <button
                className="dayone__way dayone__way--first"
                onClick={() => raise(mealForHour(new Date().getHours()), "scan")}
              >
                <span className="dayone__way-title">Scan a label or barcode</span>
                <span className="dayone__way-sub">The fast path, and what the app is for</span>
                <span className="dayone__chev" aria-hidden="true">›</span>
              </button>
              <button
                className="dayone__way"
                onClick={() => raise(mealForHour(new Date().getHours()), "search")}
              >
                <span className="dayone__way-title">Search for a food</span>
                <span className="dayone__way-sub">For anything without a package</span>
                <span className="dayone__chev" aria-hidden="true">›</span>
              </button>
              <button
                className="dayone__way"
                onClick={() => raise(mealForHour(new Date().getHours()), "quick")}
              >
                <span className="dayone__way-title">Enter calories by hand</span>
                <span className="dayone__way-sub">When you know the number and nothing else</span>
                <span className="dayone__chev" aria-hidden="true">›</span>
              </button>

              <div className="dayone__plan">
                <span className="muted">Prefer to decide meals up front?</span>
                <button className="dayone__plan-pill" onClick={() => navigate("/plan")}>
                  Plan the week
                </button>
              </div>
            </div>
          )}

          {MEALS.map((meal) => {
            const forMeal = entries.filter((entry) => entry.meal === meal);
            const kcal = forMeal.reduce((sum, entry) => sum + entry.kcal, 0);
            const expanded = open.includes(meal);

            return (
              <section className="meal" key={meal}>
                {/* The whole line is the toggle, and there is deliberately no
                    chevron: the item count and the calories already say whether
                    there is anything behind it, and a glyph whose only job is to
                    hint that a line is tappable is a glyph on every line. */}
                <button
                  className="meal__head"
                  aria-expanded={expanded}
                  onClick={() => toggle(meal)}
                >
                  <h2 className="meal__title">
                    {LABEL[meal]}
                    {/* Named only when it differs from the day on screen. A
                        section that silently wrote somewhere else would be the
                        original bug wearing a new coat. */}
                    {logTo !== local_date && (
                      <span className="meal__elsewhere">
                        {" · "}
                        {new Date(`${logTo}T12:00:00Z`).toLocaleDateString(undefined, {
                          weekday: "long",
                        })}
                      </span>
                    )}
                  </h2>
                  {/* Visible open or shut. A collapsed meal that says nothing
                      about its contents is indistinguishable from an empty one,
                      and the difference is the whole reason to collapse it. */}
                  <span className="num meal__count">
                    {forMeal.length === 0
                      ? "nothing yet"
                      : `${forMeal.length} item${forMeal.length === 1 ? "" : "s"}`}
                  </span>
                  <span className="num meal__kcal">{kcal ? Math.round(kcal) : "—"}</span>
                </button>

                {expanded && (
                  <>
                    {forMeal.map((entry) => {
                      const editingThis = editing === entry.id;

                      return (
                        /* The row and its editor, together. The editor used to
                           replace the row, which took the thing being corrected
                           off the screen at the moment of correcting it: the
                           name, the weight you are disagreeing with and what it
                           came to were all gone, and the only reading left was
                           the one you were still typing. It opens underneath
                           instead, so the before and the after are one above
                           the other. */
                        <div
                          key={entry.id}
                          className={`entry-slot${editingThis ? " is-editing" : ""}`}
                        >
                          <div className="entry">
                            {/* The whole row opens the editor, and closes it
                                again. A misread label is noticed here, looking
                                at the number, so this is where fixing it
                                belongs — and a row is a bigger target than an
                                icon. */}
                            <button
                              className="entry__body entry__open"
                              onClick={() => {
                                setEditing(editingThis ? null : entry.id);
                                // The "Fix the product" panel belongs to the
                                // editor that raised it, so it leaves with it.
                                setCorrecting(null);
                              }}
                              aria-expanded={editingThis}
                              aria-label={`Edit ${entry.name}`}
                            >
                              <div className="entry__name">
                                {waiting(entry) && (
                                  <span className="queued-dot queued-dot--row" aria-hidden="true" />
                                )}
                                {entry.name}
                              </div>
                              <div className="entry__meta num">
                                {/* Quick-added entries have no weight — the
                                    column is NOT NULL so it reads 0, which is
                                    true but not worth a line. */}
                                {entry.quantity_g > 0 && `${Math.round(entry.quantity_g)} g`}
                                {waiting(entry) ? (
                                  <span className="queued-text">
                                    {entry.quantity_g > 0 ? " · " : ""}waiting to sync
                                  </span>
                                ) : (
                                  <>
                                    {entry.quantity_g > 0 && " · "}P{" "}
                                    {Math.round(entry.protein_g)} · C {Math.round(entry.carbs_g)} ·
                                    F {Math.round(entry.fat_g)}
                                  </>
                                )}
                              </div>
                            </button>
                            <div className="entry__kcal num">{Math.round(entry.kcal)}</div>
                            <button
                              aria-label={`Remove ${entry.name}`}
                              onClick={() =>
                                remove.mutate({ id: entry.id, localDate: entry.local_date })
                              }
                              className="entry__remove"
                            >
                              ×
                            </button>
                          </div>

                          {editingThis && (
                            /* Unstyled on purpose: the editor draws its own
                               rule and the panels under it draw theirs. A
                               second `.entry-edit` around them, which is what
                               this used to be, put two hairlines a pixel apart
                               under the row. */
                            <div className="entry-slot__open">
                              <EntryEditor entry={entry} onDone={() => setEditing(null)} />

                              {/* Where the numbers came from, and the other
                                  half of what "this is wrong" can mean. The
                                  editor above re-weighs this one plate; this
                                  changes the food itself, so every future log
                                  of it is right too. Since the editor stopped
                                  taking macros, this is the only way to correct
                                  a figure — which is the point: a wrong figure
                                  is nearly always wrong about the food, not
                                  about lunch. */}
                              {entry.food_item_id && (
                                <div className="provenance">
                                  <p className="provenance__line">
                                    These macros were snapshotted when you logged it, from the food
                                    row as it read then.
                                  </p>
                                  <button
                                    className="provenance__pill"
                                    onClick={() => setCorrecting(entry.food_item_id)}
                                  >
                                    Fix the product
                                  </button>
                                </div>
                              )}

                              {correcting &&
                                correcting === entry.food_item_id &&
                                correctingFood.data && (
                                  <CorrectFood
                                    food={correctingFood.data}
                                    fromEntry
                                    onDone={() => setCorrecting(null)}
                                    onCancel={() => setCorrecting(null)}
                                  />
                                )}
                            </div>
                          )}
                        </div>
                      );
                    })}

                    {/* Which meal the sugar was in — the question the day's
                        figure cannot answer. One line, because comparing four
                        meals wants four comparable lines. */}
                    {forMeal.length > 0 && <MealNutrients totals={nutrientTotals(forMeal)} />}

                    {/* One control under an open meal, and it is a `+`.
                        Typing and scanning both live in the sheet now: a Scan
                        button beside a field, repeated under all four meals, put
                        four copies of an accented control on a screen where
                        colour is rationed, and the field itself could only ever
                        do one of the three things the sheet does. */}
                    <button
                      className="meal-add"
                      onClick={() => raise(meal)}
                      aria-label={`Add to ${LABEL[meal].toLowerCase()}`}
                    >
                      +
                    </button>
                  </>
                )}
              </section>
            );
          })}

          {offline.pending > 0 && (
            <p className="today__note">
              You can keep logging. Entries send themselves when the connection returns, and each
              one carries the day it was written for — so a sync tomorrow still files it under
              today.
            </p>
          )}
        </div>
      </div>

      {sheetMeal && (
        <AddSheet
          meal={sheetMeal}
          // The day travels with the sheet. Without it, writes land on whenever
          // the request arrives — which is today, and on a screen showing last
          // Tuesday that is the wrong day, silently.
          localDate={logTo}
          logged={loggedOnWritingDay}
          initialMode={sheetMode}
          onMeal={setSheetMeal}
          onClose={() => setSheetMeal(null)}
        />
      )}
    </div>
  );
}
